import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../../common/SharedFunctions';
import { CommonLoanSummaryInvokingFeatureType } from '../constants/CommonLoanSummaryInvokingFeatureType';
import { LoanStatusType } from '@apply/constants';
import { LoanType } from '../LoanConstants';

export interface ICommonLoanSummaryParameters {
    loan: any;
    invokingFeature: CommonLoanSummaryInvokingFeatureType;
}

export const CommonLoanSummaryAmount = ({ loan, invokingFeature }: ICommonLoanSummaryParameters) => {
    const [amountLabel, setAmountLabel] = useState('');
    const [amountValue, setAmountValue] = useState('');

    const [commitmentLabel] = useState('Commitment:');
    const [approveAmountLabel] = useState('Approved Amount:');
    const [totalCreditLineLabel] = useState('Total Credit Line:');
    const [notSetValue] = useState('Not Set');

    const styles = {
        formColumn: { paddingBottom: '3px' },
        formRow: { marginBottom: '0' },
        sectionHeader: { margin: '10px 0' },
        infoSection: {
            backgroundColor: '#c0c0c07a',
            display: 'inline-block',
            width: '100%',
            padding: '5px',
            marginTop: '5px',
            marginBottom: '10px',
        },
    };

    useEffect(() => {
        if (loan && invokingFeature != undefined) {
            renderLabel();
            renderValue();
        }
    }, [loan, invokingFeature]);

    const renderLabel = () => {
        switch (invokingFeature) {
            case CommonLoanSummaryInvokingFeatureType.PropertyInsurance:
                if (loan.loanTypeId == LoanType.MasterNote) {
                    setAmountLabel(totalCreditLineLabel);
                } else {
                    setAmountLabel(approveAmountLabel);
                }
                return;
            case CommonLoanSummaryInvokingFeatureType.BuildRentConversion:
            case CommonLoanSummaryInvokingFeatureType.RevertLoanToApprovedStatus:
                setAmountLabel(commitmentLabel);
                return;
            case CommonLoanSummaryInvokingFeatureType.LinkLoanWithBClient:
            case CommonLoanSummaryInvokingFeatureType.RestoreLoan:
                if (loan.loanTypeId == LoanType.MasterNote) {
                    setAmountLabel(totalCreditLineLabel);
                } else {
                    if (loan.statusId >= LoanStatusType.Outstanding) {
                        setAmountLabel(commitmentLabel);
                    } else {
                        setAmountLabel(approveAmountLabel);
                    }
                }
                return;
            case CommonLoanSummaryInvokingFeatureType.ReleaseHoldback:
            case CommonLoanSummaryInvokingFeatureType.RentalDscr:
            case CommonLoanSummaryInvokingFeatureType.LoanRepayment:
                setAmountLabel(commitmentLabel);
                return;
            case CommonLoanSummaryInvokingFeatureType.TerminateMasterNote:
                setAmountLabel(totalCreditLineLabel);
                return;
        }
    };

    const renderValue = () => {
        switch (invokingFeature) {
            case CommonLoanSummaryInvokingFeatureType.PropertyInsurance:
                setAmountValue(loan.commitment ? formatCurrency(loan.commitment, 0) : notSetValue);
                return;
            case CommonLoanSummaryInvokingFeatureType.BuildRentConversion:
            case CommonLoanSummaryInvokingFeatureType.RevertLoanToApprovedStatus:
                setAmountValue(loan.promissoryNote ? formatCurrency(loan.promissoryNote, 0) : notSetValue);
                return;
            case CommonLoanSummaryInvokingFeatureType.LinkLoanWithBClient:
            case CommonLoanSummaryInvokingFeatureType.RestoreLoan:
                if (loan.loanTypeId == LoanType.MasterNote) {
                    setAmountValue(loan.commitment ? formatCurrency(loan.commitment, 0) : notSetValue);
                } else {
                    if (loan.statusId >= LoanStatusType.Outstanding) {
                        setAmountValue(loan.promissoryNote ? formatCurrency(loan.promissoryNote, 0) : notSetValue);
                    } else {
                        setAmountValue(loan.commitment ? formatCurrency(loan.commitment, 0) : notSetValue);
                    }
                }
                return;
            case CommonLoanSummaryInvokingFeatureType.ReleaseHoldback:
            case CommonLoanSummaryInvokingFeatureType.RentalDscr:
            case CommonLoanSummaryInvokingFeatureType.LoanRepayment:
                if (loan.isPortfolioProperty) {
                    const propertyCommitment = loan.commitment + loan.originationFee;
                    setAmountValue(propertyCommitment ? formatCurrency(propertyCommitment, 0) : notSetValue);
                } else {
                    setAmountValue(loan.promissoryNote ? formatCurrency(loan.promissoryNote, 0) : notSetValue);
                }
                return;
            case CommonLoanSummaryInvokingFeatureType.TerminateMasterNote:
                setAmountValue(loan.commitment ? formatCurrency(loan.commitment, 0) : notSetValue);
                return;
        }
    };

    if (!loan || invokingFeature == undefined) {
        return null;
    }

    return (
        <>
            {amountLabel && (
                <>
                    <div
                        className='columns small-12 large-3'
                        style={styles.formColumn}
                    >
                        {amountLabel}
                    </div>
                    <div
                        className='columns small-12 large-9'
                        style={styles.formColumn}
                    >
                        <b>{amountValue}</b>
                    </div>
                </>
            )}
        </>
    );
};
